import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

/** The detail pane's width: dragged by its left edge, remembered per browser. */
export const DEFAULT_DETAIL_WIDTH = 440;
const MIN_WIDTH = 320;
/** What the view beside the pane keeps, so a wide drag cannot squeeze the list out. */
const MIN_VIEW = 320;
const STEP = 24;
const KEY = 'r2db-issues.detail-width';

export function useDetailWidth(): [number, (width: number) => void] {
  const [width, setWidth] = useState(() => {
    try { const saved = Number(localStorage.getItem(KEY)); return saved >= MIN_WIDTH ? saved : DEFAULT_DETAIL_WIDTH; }
    catch { return DEFAULT_DETAIL_WIDTH; }
  });
  const save = (next: number) => {
    setWidth(next);
    try { localStorage.setItem(KEY, String(Math.round(next))); } catch { /* a private window: it lasts the tab */ }
  };
  return [width, save];
}

/**
 * The left edge of the detail pane, as a vertical splitter. Drag it, or focus it and use the arrow
 * keys; a double-click puts the pane back to its default width. The pane grows to the left, so
 * moving the pointer left by N pixels widens it by N.
 */
export function ResizeHandle({ width, onResize }: { width: number; onResize: (width: number) => void }) {
  const drag = useRef<{ x: number; width: number; max: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  /** The width the pane is actually drawn at. Not `width`: CSS caps a remembered width to what the
   *  window has room for, and a drag that started from the remembered number would do nothing
   *  until the pointer had crossed the difference. */
  const shown = (from: HTMLElement) => from.parentElement?.getBoundingClientRect().width ?? width;

  /** The widest the pane may be right now: the workspace less what the view keeps. */
  const maxWidth = (from: HTMLElement) => {
    const workspace = from.closest('.workspace');
    return Math.max(MIN_WIDTH, (workspace?.getBoundingClientRect().width ?? window.innerWidth) - MIN_VIEW);
  };
  const clamp = (value: number, max: number) => Math.round(Math.min(max, Math.max(MIN_WIDTH, value)));

  // While dragging, the whole page wears the resize cursor and does not select text under it.
  useEffect(() => {
    document.body.classList.toggle('resizing', dragging);
    return () => document.body.classList.remove('resizing');
  }, [dragging]);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, width: shown(e.currentTarget), max: maxWidth(e.currentTarget) };
    setDragging(true);
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (d) onResize(clamp(d.width + (d.x - e.clientX), d.max));
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const by = e.key === 'ArrowLeft' ? STEP : e.key === 'ArrowRight' ? -STEP : 0;
    if (by) { e.preventDefault(); onResize(clamp(shown(e.currentTarget) + by, maxWidth(e.currentTarget))); }
    if (e.key === 'Home') { e.preventDefault(); onResize(DEFAULT_DETAIL_WIDTH); }
  };

  return (
    <div
      className={`resize-handle${dragging ? ' dragging' : ''}`}
      role="separator" aria-orientation="vertical" aria-label="Resize the issue pane"
      aria-valuenow={Math.round(width)} aria-valuemin={MIN_WIDTH}
      tabIndex={0} title="Drag to resize · double-click to reset"
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      onDoubleClick={() => onResize(DEFAULT_DETAIL_WIDTH)} onKeyDown={key}
    />
  );
}
